import React from 'react'
import moment from '@e3dc-react/shell/Libs/moment'
import { makeStyles } from 'tss-react/mui'
import { Divider, Grid, Paper, Box } from '@mui/material'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { NameType, Payload, ValueType } from 'recharts/types/component/DefaultTooltipContent'
import { TooltipProps } from 'recharts/types/component/Tooltip'
import { M1Sum } from '@e3dc-react/icons'
import { UnitType, UnitTypes } from 'src/Libs/types'
import { getRoundedValue } from 'src/Libs/chart-kit/pie-charts/utils/chart-calculations'
import { getDateTimeLabel } from 'src/Libs/system-data-handler/utils/chartFunctions'
import { round } from 'lodash'
import { getLocalizedNumber } from 'src/Libs/languages/functions/default'
import { ChartView, CUSTOM_UNITS, CustomTrace, TraceSuffix } from 'src/Libs/chart-kit/shared/utils/chartModel'
import { useTypedSelector } from 'src/State/RootReducer'
import { getTraceName } from 'src/Libs/system-charts/shared/utils/getTraceName'
import { LIMIT_TO_CHANGE_UNIT_TO_KW } from '../utils'
import { useWallboxHardwareByStorage } from 'src/Libs/wallboxes/hooks'

const useStyles = makeStyles()(theme => ({
  tooltipContent: {
    padding: theme.spacing(1),
    maxWidth: 400,
    [theme.breakpoints.down('sm')]: {
      fontSize: 12,
    },
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
  },
  itemColor: {
    minWidth: 20,
    maxWidth: 20,
    minHeight: 20,
    maxHeight: 20,
    borderRadius: 10,
    marginRight: theme.spacing(1),
  },
  item: {
    display: 'flex',
    alignItems: 'center',
  },
  divider: {
    marginBottom: theme.spacing(1),
    borderColor: theme.palette.divider,
  },
}))

interface CustomTooltipProps extends TooltipProps<ValueType, NameType> {
  customTraces?: CustomTrace[]
  suffixes?: TraceSuffix[]
  hasSummary?: boolean
  unit?: UnitType
  view?: ChartView
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload, label, customTraces, suffixes, hasSummary = false, unit, view }) => {
  const { t } = useCustomTranslation()
  const { classes } = useStyles()
  const { data: wallboxHardware } = useWallboxHardwareByStorage()
  const powermeterHardware = useTypedSelector(store => store.storage.powermeterHardware)
  const visibleTraces = customTraces?.filter(trace => !trace.hidden)

  /**
   * This function provide the value of the trace with the correct unit depending on the chart view
   */
  const getValue = (
    customTrace: CustomTrace,
    traceDetails: Payload<ValueType, NameType>,
    returnType: 'string' | 'number',
    suffix?: string,
    isKiloUnit?: boolean
  ): string | number => {
    let returnedValue
    const isCustomUnit = CUSTOM_UNITS.includes(traceDetails.unit as string)
    const useOriginalValue = typeof traceDetails.value !== 'number' || traceDetails.value === 0

    const formatTraceFloatValue = (traceDetails: Payload<ValueType, NameType>): ValueType | undefined => {
      if (useOriginalValue) {
        return traceDetails.value
      } else if (traceDetails.name === 'ASOC') {
        return round(traceDetails.value as number)
      } else {
        return round(traceDetails.value as number, 2)
      }
    }
    const valueToUse = formatTraceFloatValue(traceDetails) as number
    const localizedValue = getLocalizedNumber(valueToUse)

    if (isCustomUnit) {
      returnedValue = `${localizedValue} ${traceDetails.unit ?? unit}`
    } else {
      if (view === 'year') {
        if (valueToUse > LIMIT_TO_CHANGE_UNIT_TO_KW) {
          returnedValue = `${getLocalizedNumber(valueToUse / 1000, 2)} ${UnitTypes.kiloWattHour}`
        } else {
          returnedValue = `${localizedValue} ${UnitTypes.wattHour}`
        }
      } else if (view === 'month') {
        returnedValue = `${localizedValue} ${UnitTypes.wattHour}`
      } else {
        returnedValue = `${localizedValue} ${UnitTypes.watt}`
      }
    }

    switch (returnType) {
      case 'string': {
        const traceName = getTraceName(t, customTrace, wallboxHardware, powermeterHardware)
        if (suffix) return `${traceName}-${suffix}: ${returnedValue}`
        return `${traceName}: ${returnedValue}`
      }
      case 'number': {
        return getRoundedValue(valueToUse as number, isKiloUnit)
      }
      default: {
        return getRoundedValue(valueToUse as number, isKiloUnit)
      }
    }
  }

  const getTraceDetails = (customTrace: CustomTrace, suffix?: TraceSuffix): Payload<ValueType, NameType> | undefined => {
    const simpleDetailKey = customTrace.alternateKey ?? customTrace.key
    const detailKey = suffix ? `${simpleDetailKey}-${suffix.name}` : simpleDetailKey
    return payload?.find(payload => payload.name === detailKey || payload.name === `f-${detailKey}`)
  }

  const getContent = (customTrace: CustomTrace, suffix?: TraceSuffix, index?: number): React.ReactNode => {
    const traceDetails = getTraceDetails(customTrace, suffix)
    const suffixColor = suffix && index !== undefined ? customTrace.suffixColors?.[index] : undefined

    if (traceDetails?.value || traceDetails?.value === 0) {
      return (
        <Grid
          item
          xs={!!visibleTraces && visibleTraces.length > 6 ? 6 : 12}
          key={customTrace.alternateKey ?? customTrace.key}
          className={classes.item}
        >
          <div className={classes.itemColor} style={{ backgroundColor: suffixColor?.color ?? traceDetails?.color }} />
          {getValue(customTrace, traceDetails, 'string', suffix?.name, unit?.includes('k'))}
        </Grid>
      )
    }

    return null
  }

  if (active) {
    const sum = visibleTraces?.reduce((prev, currTrace) => {
      const traceDetails = getTraceDetails(currTrace)
      if (traceDetails) {
        return prev + (getValue(currTrace, traceDetails, 'number', undefined, unit?.includes('k')) as number)
      }
      return prev
    }, 0)
    const sumString = `${Math.round((sum ?? 0) * 100) / 100} ${unit ?? ''}`

    return (
      <Paper className={classes.tooltipContent}>
        <Grid container>
          <Grid item xs={12}>
            {getDateTimeLabel(moment(label), t, view)}
          </Grid>
          {visibleTraces?.map(customTrace => {
            if (suffixes?.length) {
              return suffixes.map((suffix, index) => getContent(customTrace, suffix, index))
            }

            return getContent(customTrace)
          })}
          {hasSummary && (
            <Box width={'100%'} ml={2} mt={2}>
              <Divider className={classes.divider} />
              <Box display="flex" alignItems="center">
                <M1Sum />
                {sumString}
              </Box>
            </Box>
          )}
        </Grid>
      </Paper>
    )
  }

  return null
}

export default CustomTooltip
